import { gql } from '@apollo/client';

export const metadataColumnFragments = {
  metadataColumn: gql`
    fragment metadataColumn on MetadataColumn {
      id
      name
      memberName
      description
      alternateDescription
      ordinalPosition
      relatedMemberName
      relatedMemberDisplayField
      columnHeading
      alternateColumnHeading
      columnWidth
      maxColumnWidth
      decimals
      dataType
      requiredData
      columnPrompt
      alternateColumnPrompt
      isUserEditable
      pickListType
      pickListName
      minValue
      maxValue
      defaultValue
      changeControl
      displayColumn {
        id
        name
        columnWidth
        minValue
        maxValue
        decimals
        dataType
      }
    }
  `,
  commonSearchMetadataFields: gql`
    fragment commonSearchMetadataFields on MetadataColumn {
      id
      memberName
      description
      metadataTable {
        memberName
      }
    }
  `,
};

export const GET_TABLE_METADATA = gql`
  ${metadataColumnFragments.metadataColumn}
  query tableMetadata($name: String!, $projectId: ID!) {
    tableMetadata(name: $name, projectId: $projectId) {
      id
      name
      memberName
      description
      alternateDescription
      projectId
      sortOrder
      metadataColumns {
        ...metadataColumn
      }
      relatedDisplayColumns {
        ...metadataColumn
      }
      userAccountLayouts {
        id
        layoutName
        description
        projectId
        userAccountId
        selectClause
        whereClause
        orderByClause
      }
      systemAccountLayouts {
        id
        layoutName
        description
        projectId
        userAccountId
        selectClause
        whereClause
        orderByClause
      }
    }
  }
`;

export const SEARCH_METADATA_TABLES = gql`
  query searchMetadataTables($searchInput: SearchInput) {
    searchMetadataTables(searchInput: $searchInput) {
      items {
        id
        description
        alternateDescription
        memberName
        editList
        browseList
      }
    }
  }
`;

export const SAVE_METADATA_TABLES = gql`
  mutation SaveMetadataTables($saveMetadataTablesInput: SaveMetadataTablesInput!) {
    saveMetadataTables(saveMetadataTablesInput: $saveMetadataTablesInput) {
      metadataTables {
        id
        description
        alternateDescription
        memberName
      }
      errors {
        messageKeys
        name
      }
    }
  }
`;

export const SAVE_USER_LAYOUT = gql`
  mutation SaveUserAccountLayout($saveUserAccountLayoutInput: SaveUserAccountLayoutInput!) {
    saveUserAccountLayout(saveUserAccountLayoutInput: $saveUserAccountLayoutInput) {
      userAccountLayout {
        id
        description
        selectClause
        whereClause
        orderByClause
      }
      errors
    }
  }
`;

export const DELETE_USER_LAYOUT = gql`
  mutation DeleteUserAccountLayout($deleteUserAccountLayoutInput: DeleteUserAccountLayoutInput!) {
    deleteUserAccountLayout(deleteUserAccountLayoutInput: $deleteUserAccountLayoutInput) {
      id
      errors
    }
  }
`;

export const SEARCH_METADATA_COLUMNS_REVISED_CARD = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  query SearchMetadataColumns($searchInput: SearchInput) {
    searchMetadataColumns(searchInput: $searchInput) {
      items {
        ...commonSearchMetadataFields
        revisedCard
      }
    }
  }
`;

export const SAVE_METADATA_COLUMNS_REVISED_CARD = gql`
  mutation SaveMetadataColumns($saveMetadataColumnsInput: SaveMetadataColumnsInput!) {
    saveMetadataColumns(saveMetadataColumnsInput: $saveMetadataColumnsInput) {
      metadataColumns {
        id
        revisedCard
      }
      errors {
        messageKeys
        name
      }
    }
  }
`;
export const SEARCH_METADATA_COLUMNS_OPTIONAL_CARD = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  query SearchMetadataColumns($searchInput: SearchInput) {
    searchMetadataColumns(searchInput: $searchInput) {
      items {
        ...commonSearchMetadataFields
        allowedOnCards
        printOnCards
      }
    }
  }
`;

export const SEARCH_METADATA_COLUMNS_WITH_REQUIRED_FIELD = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  query SearchMetadataColumns($searchInput: SearchInput) {
    searchMetadataColumns(searchInput: $searchInput) {
      items {
        ...commonSearchMetadataFields
        requiredData
      }
    }
  }
`;

export const SAVE_METADATA_COLUMNS_OPTIONAL_CARD = gql`
  mutation SaveMetadataColumns($saveMetadataColumnsInput: SaveMetadataColumnsInput!) {
    saveMetadataColumns(saveMetadataColumnsInput: $saveMetadataColumnsInput) {
      metadataColumns {
        id
        printOnCards
      }
      errors {
        messageKeys
        name
      }
    }
  }
`;

export const SEARCH_METADATA_COLUMNS_WITH_CHANGE_CONTROL = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  query SearchMetadataColumns($searchInput: SearchInput) {
    searchMetadataColumns(searchInput: $searchInput) {
      items {
        ...commonSearchMetadataFields
        changeControl
      }
    }
  }
`;

export const SEARCH_METADATA_COLUMNS_SELECT_LIST = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  query SearchMetadataColumns($searchInput: SearchInput) {
    searchMetadataColumns(searchInput: $searchInput) {
      items {
        ...commonSearchMetadataFields
        pickListType
        pickListName
      }
    }
  }
`;

export const SAVE_REQUIRED_INPUT_COLUMNS = gql`
  mutation SaveMetadataColumns($saveMetadataColumnsInput: SaveMetadataColumnsInput!) {
    saveMetadataColumns(saveMetadataColumnsInput: $saveMetadataColumnsInput) {
      metadataColumns {
        id
        requiredData
      }
      errors {
        name
        messageKeys
      }
    }
  }
`;

export const SAVE_CHANGE_CONTROL_COLUMNS = gql`
  mutation SaveMetadataColumns($saveMetadataColumnsInput: SaveMetadataColumnsInput!) {
    saveMetadataColumns(saveMetadataColumnsInput: $saveMetadataColumnsInput) {
      metadataColumns {
        id
        changeControl
      }
      errors {
        name
        messageKeys
      }
    }
  }
`;

export const SEARCH_METADATA_COLUMNS_UI = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  query SearchMetadataColumns($searchInput: SearchInput) {
    searchMetadataColumns(searchInput: $searchInput) {
      items {
        ...commonSearchMetadataFields
        alternateColumnHeading
        alternateColumnPrompt
        alternateDescription
        columnHeading
        columnPrompt
      }
    }
  }
`;

export const SAVE_METADATA_COLUMNS_UI = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  mutation SaveMetadataColumns($saveMetadataColumnsInput: SaveMetadataColumnsInput!) {
    saveMetadataColumns(saveMetadataColumnsInput: $saveMetadataColumnsInput) {
      metadataColumns {
        ...commonSearchMetadataFields
        id
        alternateColumnHeading
        alternateColumnPrompt
        alternateDescription
        columnHeading
        columnPrompt
      }
      errors {
        messageKeys
        name
      }
    }
  }
`;

export const SAVE_METADATA_COLUMNS_SELECT_LIST = gql`
  mutation SaveMetadataColumns($saveMetadataColumnsInput: SaveMetadataColumnsInput!) {
    saveMetadataColumns(saveMetadataColumnsInput: $saveMetadataColumnsInput) {
      metadataColumns {
        id
        pickListType
        pickListName
      }
      errors {
        messageKeys
        tag: name
      }
    }
  }
`;

export const SEARCH_METADATA_COLUMNS_WITH_COLUMN_WIDTH = gql`
  ${metadataColumnFragments.commonSearchMetadataFields}
  query SearchMetadataColumns($searchInput: SearchInput) {
    searchMetadataColumns(searchInput: $searchInput) {
      items {
        ...commonSearchMetadataFields
        columnWidth
        maxColumnWidth
        metadataColumnView {
          maxUsedWidth
        }
      }
    }
  }
`;

export const SAVE_COLUMN_WIDTH = gql`
  mutation SaveMetadataColumns($saveMetadataColumnsInput: SaveMetadataColumnsInput!) {
    saveMetadataColumns(saveMetadataColumnsInput: $saveMetadataColumnsInput) {
      metadataColumns {
        id
        columnWidth
      }
      errors {
        name
        messageKeys
      }
    }
  }
`;
